import { useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ReconnectingWebSocket from "reconnecting-websocket";
import { useCrossAccountProjects } from "../../lib/react-query/crossAccount";
import { useProjectDetails } from "../../lib/react-query/tasks";
import {
  useDraftFlowContract,
  useDraftFlowMessages,
  useDraftFlowSessionDetail,
  useDraftFlowSessions,
  useCreateDraftFlowSession,
  useSendDraftFlowMessage,
  useUpdateDraftFlowSession,
  useStopDraftFlowSession,
  useAIChatGrant,
} from "../../lib/react-query/aiChat";
import { queryKeys } from "../../redux/network/constant";
import {
  normalizeWebSocketUrl,
  webSocketUrl,
} from "../../redux/socket/constants.js";
import { useDraftFlowStore, useShallow } from "../../stores/draftFlowStore";
import { showToast } from "../shared";
import { extractBackendErrorMessage } from "../../utils/utils";
import { getDraftFlowRoute } from "./draftFlowRoutes";
import { includeSelectedProject } from "./draftFlowProjectContext";
import { isContractInProgress } from "./contractStates";

const MESSAGES_PAGE = 1;
const MESSAGES_PAGE_SIZE = 200;

const normalizeSelection = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return String(value);
};

const normalizeList = (data) =>
  Array.isArray(data?.items) ? data.items : [];

const extractContractsFromMessages = (messages) => {
  if (!Array.isArray(messages)) return [];

  const contracts = [];
  const seenContractIds = new Set();

  messages.forEach((message) => {
    if (message?.message_type === 'contract_ready' && message?.metadata) {
      const contractId = message.metadata.contract_id;
      if (contractId && !seenContractIds.has(contractId)) {
        seenContractIds.add(contractId);
        contracts.push({
          id: contractId,
          title: message.metadata.title,
          description: message.metadata.description,
          file_link: message.metadata.file_link,
          status: message.metadata.status,
          created_at: message.created_at,
          message_id: message.id,
        });
      }
    }
  });

  // Sort by created_at desc (newest first)
  return contracts.sort((a, b) =>
    new Date(b.created_at) - new Date(a.created_at)
  );
};

const getMessageRole = (message) => {
  const base =
    message?.role ||
    message?.sender ||
    message?.sender_type ||
    (message?.is_assistant ? "assistant" : message?.is_user ? "user" : "");
  return String(base || "").toLowerCase();
};

const normalizeMessage = (message, fallbackRole) => {
  if (!message) return null;
  const metadata = message?.metadata || {};
  const failed =
    message?.status === "error" ||
    message?.processing_status === "failed" ||
    metadata?.agent_run_failed === true;
  return {
    ...message,
    role: message?.role || fallbackRole,
    status: failed ? "error" : message?.status,
    prompt: message?.prompt || metadata?.retry_prompt,
  };
};

const emptyCache = { items: [], total: 0 };

const useMessagesCache = (activeSessionId) => {
  const queryClient = useQueryClient();

  const updateMessagesCache = useCallback(
    (updater, sessionId = activeSessionId) => {
      const messagesQueryKey = [
        ...queryKeys.AIChatMessages(sessionId, MESSAGES_PAGE),
        MESSAGES_PAGE_SIZE,
      ];
      queryClient.setQueryData(messagesQueryKey, (prev) => {
        const current = prev || emptyCache;
        return updater(current);
      });
    },
    [activeSessionId, queryClient],
  );

  return { updateMessagesCache };
};

const useDraftFlowData = () => {
  const { projectId } = useParams();
  const { activeSessionId, contractId } = useDraftFlowStore(useShallow((s) => ({
    activeSessionId: s.activeSessionId,
    contractId: s.contractId,
  })));
  const routeProjectId = Number(projectId);
  const hasRouteProject = Number.isFinite(routeProjectId) && routeProjectId > 0;

  // Fetch cross-account projects (no team filtering)
  const { data: projectsData, isLoading: projectsLoading } = useCrossAccountProjects(
    { page_size: 100, ordering: "-updated_at" },
    true,
  );
  const {
    data: routeProject,
    isLoading: routeProjectLoading,
  } = useProjectDetails(
    hasRouteProject ? routeProjectId : null,
    hasRouteProject,
  );

  const projects = useMemo(
    () => includeSelectedProject(projectsData?.items, routeProject),
    [projectsData, routeProject],
  );

  const projectOptions = useMemo(() => {
    return projects
      .filter((project) => project?.user_membership_status !== "PENDING")
      .map((project) => ({
        id: String(project?.id),
        name: project?.title || project?.name || `Project ${project?.id}`,
      }));
  }, [projects]);

  const projectsById = useMemo(() => {
    return projects.reduce((acc, project) => {
      const key = String(project?.id);
      acc[key] = project;
      return acc;
    }, {});
  }, [projects]);

  const { data: sessionsData, isLoading: sessionsLoading } = useDraftFlowSessions(
    null,
    true,
  );
  const sessions = useMemo(
    () => normalizeList(sessionsData).map((session) => ({
      ...session,
      project_id: session?.project_id ?? session?.project ?? null,
    })),
    [sessionsData],
  );

  const {
    data: sessionDetail,
    isLoading: sessionDetailLoading,
  } = useDraftFlowSessionDetail(activeSessionId, true);

  const {
    data: messagesData,
    isLoading: messagesLoading,
    isFetching: messagesFetching,
  } = useDraftFlowMessages(activeSessionId, MESSAGES_PAGE, MESSAGES_PAGE_SIZE, true);

  const {
    data: contractDetail,
    isLoading: contractLoading,
    isFetching: contractFetching,
  } = useDraftFlowContract(contractId, true);

  const activeSession = useMemo(() => {
    if (!activeSessionId) return null;
    const fromList = sessions.find(
      (session) => String(session?.id) === String(activeSessionId),
    );
    return fromList || sessionDetail || null;
  }, [activeSessionId, sessions, sessionDetail]);

  const displayMessages = useMemo(() => {
    if (!activeSessionId) return [];
    return normalizeList(messagesData).filter(
      (m) => getMessageRole(m) !== "system",
    );
  }, [messagesData, activeSessionId]);

  const contracts = useMemo(() => {
    return extractContractsFromMessages(displayMessages);
  }, [displayMessages]);

  return {
    data: {
      projectOptions,
      projectsById,
      sessions,
      displayMessages,
      contracts,
      sessionDetail,
      contractDetail,
      activeSession,
    },
    loading: {
      projectsLoading: projectsLoading || routeProjectLoading,
      sessionsLoading,
      sessionDetailLoading,
      messagesLoading: messagesLoading || (messagesFetching && !messagesData),
      contractLoading: contractLoading || contractFetching,
    },
  };
};

const useDraftFlowProgressContext = () => {
  const { contractState } = useDraftFlowStore(useShallow((s) => ({
    contractState: s.contractState,
  })));
  return { contractState };
};

const useDraftFlowActions = () => {
  const navigate = useNavigate();
  const { projectId, sessionId } = useParams();
  const { data, loading } = useDraftFlowData();
  const {
    selectedProjectId,
    activeSessionId,
    pendingAssistant,
    pendingMessageId,
    sandboxProgressByJobId,
    agentProgress,
    editingTitle,
    showContractPanel,
    contractPanelWidth,
    setSelectedProjectId,
    setIntentData,
    setPendingAssistant,
    setPendingMessageId,
    setEditingSessionId,
    setEditingTitle,
    setShowSidebar,
    setShowContractPanel,
    setContractPanelWidth,
  } = useDraftFlowStore(useShallow((s) => ({
    selectedProjectId: s.selectedProjectId,
    activeSessionId: s.activeSessionId,
    pendingAssistant: s.pendingAssistant,
    pendingMessageId: s.pendingMessageId,
    sandboxProgressByJobId: s.sandboxProgressByJobId,
    agentProgress: s.agentProgress,
    editingTitle: s.editingTitle,
    showContractPanel: s.showContractPanel,
    contractPanelWidth: s.contractPanelWidth,
    setSelectedProjectId: s.setSelectedProjectId,
    setIntentData: s.setIntentData,
    setPendingAssistant: s.setPendingAssistant,
    setPendingMessageId: s.setPendingMessageId,
    setEditingSessionId: s.setEditingSessionId,
    setEditingTitle: s.setEditingTitle,
    setShowSidebar: s.setShowSidebar,
    setShowContractPanel: s.setShowContractPanel,
    setContractPanelWidth: s.setContractPanelWidth,
  })));

  const scrollRef = useRef(null);
  const messageInputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const resolvedProjectEntryRef = useRef("");
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);

  const { updateMessagesCache } = useMessagesCache(activeSessionId);

  const { mutateAsync: createSession } = useCreateDraftFlowSession();
  const { mutateAsync: updateSession } = useUpdateDraftFlowSession();
  const { mutateAsync: sendMessage } = useSendDraftFlowMessage();
  const { mutateAsync: stopSession } = useStopDraftFlowSession();

  const isNearBottomRef = useRef(true);
  const wasPendingAssistantRef = useRef(false);
  const handleChatScroll = useCallback((e) => {
    const el = e.currentTarget;
    isNearBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  }, []);

  useEffect(() => {
    const assistantJustFinished =
      wasPendingAssistantRef.current && !pendingAssistant;
    const shouldScroll = isNearBottomRef.current || assistantJustFinished;
    wasPendingAssistantRef.current = pendingAssistant;
    if (!shouldScroll) return;

    requestAnimationFrame(() => {
      const sentinel = scrollRef.current;
      const container = sentinel?.parentElement;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      } else {
        sentinel?.scrollIntoView({ behavior: "smooth" });
      }
    });
  }, [data.displayMessages, pendingAssistant, sandboxProgressByJobId, agentProgress]);

  const getProjectRoute = useCallback(getDraftFlowRoute, []);

  const handleProjectChange = useCallback(
    (eventOrValue) => {
      const value = normalizeSelection(eventOrValue?.target?.value ?? eventOrValue);
      setSelectedProjectId(value || "");
    },
    [setSelectedProjectId],
  );

  const handleCreateSession = useCallback(async ({ projectId, silent } = {}) => {
    try {
      const resolvedProjectId = projectId ?? selectedProjectId;
      const payload = resolvedProjectId
        ? { project_id: Number(resolvedProjectId) }
        : {};
      const response = await createSession({ payload });
      const newSessionId = response?.id || response?.session_id;
      if (newSessionId) {
        navigate(getProjectRoute(resolvedProjectId, newSessionId));
      }
      return { id: newSessionId, projectId: resolvedProjectId };
    } catch (err) {
      const message = extractBackendErrorMessage(
        err,
        "Failed to create session",
      );
      if (!silent) {
        showToast(message, "error", { position: "top-center" });
      }
      throw err;
    }
  }, [createSession, selectedProjectId, navigate, getProjectRoute]);

  useEffect(() => {
    if (loading.sessionsLoading || !projectId || sessionId || resolvedProjectEntryRef.current === String(projectId)) {
      return;
    }
    resolvedProjectEntryRef.current = String(projectId);
    const latestSession = data.sessions.find(
      (session) => String(session?.project_id) === String(projectId) && session?.status !== "ARCHIVED",
    );
    if (latestSession) {
      navigate(getProjectRoute(projectId, latestSession.id), { replace: true });
      return;
    }
    handleCreateSession({ projectId, silent: true });
  }, [data.sessions, getProjectRoute, handleCreateSession, loading.sessionsLoading, navigate, projectId, sessionId]);

  const handleSessionSelect = useCallback(
    (sessionIdValue) => {
      navigate(getProjectRoute(selectedProjectId, sessionIdValue));
    },
    [navigate, selectedProjectId, getProjectRoute],
  );

  const handleStartRename = useCallback(
    (session) => {
      setEditingSessionId(session?.id);
      setEditingTitle(session?.title || "Untitled Session");
    },
    [setEditingSessionId, setEditingTitle],
  );

  const handleSaveRename = useCallback(
    async (session) => {
      if (!editingTitle?.trim()) {
        showToast("Session title is required", "error", {
          position: "top-center",
        });
        return;
      }
      try {
        await updateSession({
          sessionId: session?.id,
          projectId: selectedProjectId,
          payload: { title: editingTitle.trim() },
        });
        setEditingSessionId(null);
        setEditingTitle("");
      } catch (err) {
        const message = extractBackendErrorMessage(
          err,
          "Failed to rename session",
        );
        showToast(message, "error", { position: "top-center" });
      }
    },
    [editingTitle, selectedProjectId, setEditingSessionId, setEditingTitle, updateSession],
  );

  const handleArchiveSession = useCallback(
    async (session) => {
      try {
        await updateSession({
          sessionId: session?.id,
          projectId: selectedProjectId,
          payload: { status: "ARCHIVED" },
        });
        if (String(activeSessionId) === String(session?.id)) {
          navigate(getProjectRoute(selectedProjectId));
        }
      } catch (err) {
        const message = extractBackendErrorMessage(
          err,
          "Failed to archive session",
        );
        showToast(message, "error", { position: "top-center" });
      }
    },
    [activeSessionId, selectedProjectId, navigate, updateSession, getProjectRoute],
  );

  const processMessageRequest = useCallback(
    async ({ content, clientMessageId, optimisticUserMessage, sessionId: overrideSessionId }) => {
      const resolvedSessionId = overrideSessionId || activeSessionId;
      abortControllerRef.current = new AbortController();

      try {
        const response = await sendMessage({
          sessionId: resolvedSessionId,
          payload: { content, client_message_id: clientMessageId },
          signal: abortControllerRef.current.signal,
        });

        abortControllerRef.current = null;

        if (response?.message === "Message cancelled" && !response?.assistant_message && !response?.assistantMessage) {
          updateMessagesCache((prev) => {
            const cleaned = prev.items.filter(
              (m) => !(m?.status === "loading" && m?.client_message_id === clientMessageId),
            );
            return { ...prev, items: cleaned, total: cleaned.length };
          }, resolvedSessionId);
          setPendingAssistant(false);
          setPendingMessageId(null);
          return;
        }

        const userMessage = optimisticUserMessage
          ? normalizeMessage(
              response?.user_message || response?.userMessage,
              "user",
            )
          : null;
        const assistantMessage = normalizeMessage(
          response?.assistant_message || response?.assistantMessage,
          "assistant",
        );
        updateMessagesCache((prev) => {
          const cleaned = prev.items.filter(
            (m) =>
              m?.client_message_id !== clientMessageId &&
              !(userMessage?.id && m?.id === userMessage.id) &&
              !(assistantMessage?.id && m?.id === assistantMessage.id),
          );
          if (optimisticUserMessage) {
            cleaned.push(userMessage
              ? { ...userMessage, client_message_id: clientMessageId }
              : optimisticUserMessage
            );
          }
          if (assistantMessage) {
            cleaned.push({ ...assistantMessage, client_message_id: clientMessageId });
          }
          return { ...prev, items: cleaned, total: cleaned.length };
        }, resolvedSessionId);
        setPendingAssistant(false);
        setPendingMessageId(null);
        if (response?.intent) {
          setIntentData(response.intent);
        }
      } catch (err) {
        abortControllerRef.current = null;

        if (err?.name === "CanceledError" || err?.name === "AbortError" || err?.code === "ERR_CANCELED") {
          updateMessagesCache((prev) => {
            const cleaned = prev.items.filter(
              (m) => !(m?.status === "loading" && m?.client_message_id === clientMessageId),
            );
            return { ...prev, items: cleaned, total: cleaned.length };
          }, resolvedSessionId);
          setPendingAssistant(false);
          setPendingMessageId(null);
          return;
        }

        const message = extractBackendErrorMessage(
          err,
          "Failed to send message",
        );
        updateMessagesCache((prev) => ({
          ...prev,
          items: prev.items.map((item) =>
            item?.client_message_id === clientMessageId && item?.status === "loading"
              ? { ...item, status: "error", error: message }
              : item,
          ),
        }), resolvedSessionId);
        setPendingAssistant(false);
        setPendingMessageId(null);
        showToast(message, "error", { position: "top-center" });
      }
    },
    [activeSessionId, sendMessage, setIntentData, updateMessagesCache, setPendingAssistant, setPendingMessageId],
  );

  const handleSendMessage = useCallback(async (inputValue) => {
    if (pendingAssistant) return;
    const trimmed = (inputValue ?? "").trim();
    if (!trimmed) return;
    let ensuredSessionId = activeSessionId;
    if (!ensuredSessionId) {
      try {
        const payload = selectedProjectId
          ? { project_id: Number(selectedProjectId) }
          : {};
        const params = selectedProjectId ? null : { skip_welcome: "yes" };
        const response = await createSession({ payload, params });
        ensuredSessionId = response?.id || response?.session_id;
        if (ensuredSessionId) {
          navigate(getProjectRoute(selectedProjectId, ensuredSessionId));
        }
      } catch (err) {
        const message = extractBackendErrorMessage(
          err,
          "Failed to start session",
        );
        showToast(message, "error", { position: "top-center" });
        return;
      }
      if (!ensuredSessionId) {
        showToast("Unable to start a session", "error", {
          position: "top-center",
        });
        return;
      }
    }
    const clientMessageId = `client-${Date.now()}`;
    const optimisticUserMessage = {
      role: "user",
      content: trimmed,
      created_at: new Date().toISOString(),
      client_message_id: clientMessageId,
    };
    const loadingMessage = {
      role: "assistant",
      status: "loading",
      prompt: trimmed,
      created_at: new Date().toISOString(),
      client_message_id: clientMessageId,
    };
    setPendingAssistant(true);
    setPendingMessageId(clientMessageId);
    updateMessagesCache((prev) => ({
      ...prev,
      items: [...prev.items, optimisticUserMessage, loadingMessage],
    }), ensuredSessionId);
    await processMessageRequest({
      content: trimmed,
      clientMessageId,
      optimisticUserMessage,
      sessionId: ensuredSessionId,
    });
  }, [activeSessionId, pendingAssistant, processMessageRequest, updateMessagesCache, setPendingAssistant, setPendingMessageId, selectedProjectId, createSession, navigate, getProjectRoute]);

  const handleRetryMessage = useCallback(
    async (prompt, clientMessageId) => {
      if (!activeSessionId || pendingAssistant || !prompt) return;
      setPendingAssistant(true);
      setPendingMessageId(clientMessageId);
      const optimisticUserMessage = {
        role: "user",
        content: prompt,
        created_at: new Date().toISOString(),
        client_message_id: clientMessageId,
      };
      updateMessagesCache((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item?.client_message_id === clientMessageId && item?.status === "error"
            ? { ...item, status: "loading", error: null, created_at: new Date().toISOString() }
            : item,
        ),
      }));
      await processMessageRequest({
        content: prompt,
        clientMessageId,
        optimisticUserMessage,
      });
    },
    [activeSessionId, pendingAssistant, processMessageRequest, updateMessagesCache, setPendingAssistant, setPendingMessageId],
  );

  const handleStopGeneration = useCallback(async () => {
    if (!activeSessionId || !pendingAssistant) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    try {
      await stopSession(activeSessionId);
    } catch (err) {
      console.warn("Failed to call stop endpoint:", err);
    }

    if (pendingMessageId) {
      updateMessagesCache((prev) => {
        const cleaned = prev.items.filter(
          (m) => !(m?.status === "loading" && m?.client_message_id === pendingMessageId),
        );
        return { ...prev, items: cleaned, total: cleaned.length };
      });
    }

    setPendingAssistant(false);
    setPendingMessageId(null);
  }, [activeSessionId, pendingAssistant, stopSession, updateMessagesCache, pendingMessageId, setPendingAssistant, setPendingMessageId]);

  const toggleSidebar = useCallback(() => {
    setShowSidebar((prev) => !prev);
  }, [setShowSidebar]);

  const toggleContractPanel = useCallback(() => {
    setShowContractPanel((prev) => !prev);
  }, [setShowContractPanel]);

  const handleResizeStart = useCallback((e) => {
    if (!showContractPanel) return;
    e.preventDefault();
    e.stopPropagation();
    isResizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = contractPanelWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [showContractPanel, contractPanelWidth]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingRef.current) return;
      const delta = resizeStartXRef.current - e.clientX;
      setContractPanelWidth(resizeStartWidthRef.current + delta);
    };
    const handleMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [setContractPanelWidth]);

  return {
    handleProjectChange,
    handleCreateSession,
    handleSessionSelect,
    handleStartRename,
    handleSaveRename,
    handleArchiveSession,
    handleSendMessage,
    handleRetryMessage,
    handleStopGeneration,
    handleChatScroll,
    toggleSidebar,
    toggleContractPanel,
    handleResizeStart,
    scrollRef,
    messageInputRef,
  };
};

const DraftFlowProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const { projectId, sessionId } = useParams();
  const accessToken = useSelector((state) => state?.user?.user?.token?.access);

  const {
    activeSessionId,
    contractId,
    pendingMessageId,
    setActiveSessionId,
    setSelectedProjectId,
    setContractState,
    setContractId,
    setPendingAssistant,
    setPendingMessageId,
    setSandboxProgress,
    clearSandboxProgress,
    setAgentProgress,
    clearAgentProgress,
    setIntentData,
    setGrant,
    setGrantLoading,
    setSummaryMeta,
    resetSessionState,
  } = useDraftFlowStore(useShallow((s) => ({
    activeSessionId: s.activeSessionId,
    contractId: s.contractId,
    pendingMessageId: s.pendingMessageId,
    setActiveSessionId: s.setActiveSessionId,
    setSelectedProjectId: s.setSelectedProjectId,
    setContractState: s.setContractState,
    setContractId: s.setContractId,
    setPendingAssistant: s.setPendingAssistant,
    setPendingMessageId: s.setPendingMessageId,
    setSandboxProgress: s.setSandboxProgress,
    clearSandboxProgress: s.clearSandboxProgress,
    setAgentProgress: s.setAgentProgress,
    clearAgentProgress: s.clearAgentProgress,
    setIntentData: s.setIntentData,
    setGrant: s.setGrant,
    setGrantLoading: s.setGrantLoading,
    setSummaryMeta: s.setSummaryMeta,
    resetSessionState: s.resetSessionState,
  })));

  const socketRef = useRef(null);

  const { data } = useDraftFlowData();
  const { sessionDetail, contractDetail } = data;

  const { updateMessagesCache } = useMessagesCache(activeSessionId);

  const { refetch: refetchMessages } = useDraftFlowMessages(
    activeSessionId,
    MESSAGES_PAGE,
    MESSAGES_PAGE_SIZE,
    true,
  );

  const { refetch: refetchContract } = useDraftFlowContract(contractId, true);
  const { data: grantData, isLoading: grantLoading } = useAIChatGrant(
    activeSessionId,
    true,
  );

  useEffect(() => {
    const nextSessionId = sessionId ? String(sessionId) : null;
    if (nextSessionId !== String(activeSessionId || "")) {
      setActiveSessionId(nextSessionId);
    }
  }, [sessionId, activeSessionId, setActiveSessionId]);

  useEffect(() => {
    setSelectedProjectId(projectId ? String(projectId) : "");
  }, [projectId, setSelectedProjectId]);

  useEffect(() => {
    resetSessionState();
  }, [activeSessionId, resetSessionState]);

  useEffect(() => {
    setGrant(grantData || null);
    setGrantLoading(grantLoading);
  }, [grantData, grantLoading, setGrant, setGrantLoading]);

  useEffect(() => {
    if (!sessionDetail) {
      setSummaryMeta(null);
      return;
    }
    setSummaryMeta({
      version: sessionDetail?.context_summary_version || 0,
      created_at: sessionDetail?.context_summary_created_at || null,
      error: sessionDetail?.context_summary_error || "",
    });
  }, [sessionDetail, setSummaryMeta]);

  const initialContractSyncedRef = useRef(false);
  useEffect(() => {
    if (!sessionDetail || initialContractSyncedRef.current) return;
    const nextContractId = sessionDetail?.contract_id || null;
    if (nextContractId) {
      initialContractSyncedRef.current = true;
      setContractId(nextContractId);
    }
  }, [sessionDetail, setContractId]);

  useEffect(() => {
    initialContractSyncedRef.current = false;
  }, [activeSessionId]);

  useEffect(() => {
    if (!contractDetail) return;
    const status = String(contractDetail?.status || "").toLowerCase();
    if (status === "failed" || status === "expired") {
      setContractState((prev) => ({
        ...prev,
        status: "failed",
        error:
          contractDetail?.error ||
          (status === "expired"
            ? "Contract generation timed out"
            : "Contract generation failed"),
      }));
      return;
    }
    if (status === "pending") {
      setContractState((prev) => ({
        ...prev,
        status: "initializing",
        progress: 0,
        progressMessage: "Contract generation in progress...",
        error: null,
      }));
      return;
    }
    if (
      status === "successful" ||
      status === "completed" ||
      contractDetail?.file_link ||
      contractDetail?.file_url ||
      contractDetail?.file
    ) {
      setContractState((prev) => ({
        ...prev,
        status: "completed",
        error: null,
      }));
    }
  }, [contractDetail, setContractState]);

  const contractIdRef = useRef(contractId);
  const pendingMessageIdRef = useRef(pendingMessageId);
  const refetchContractRef = useRef(refetchContract);
  const refetchMessagesRef = useRef(refetchMessages);
  useEffect(() => { contractIdRef.current = contractId; }, [contractId]);
  useEffect(() => { pendingMessageIdRef.current = pendingMessageId; }, [pendingMessageId]);
  useEffect(() => { refetchContractRef.current = refetchContract; }, [refetchContract]);
  useEffect(() => { refetchMessagesRef.current = refetchMessages; }, [refetchMessages]);

  useEffect(() => {
    if (!activeSessionId || !webSocketUrl || !accessToken) return;
    const socketUrl = new URL(
      `ws/ai-chat/${activeSessionId}/`,
      `${normalizeWebSocketUrl(webSocketUrl)}/`,
    ).toString();
    const socket = new ReconnectingWebSocket(socketUrl, ["access_token", accessToken]);
    socketRef.current = socket;
    let hasConnectedBefore = false;

    socket.onopen = () => {
      if (hasConnectedBefore) {
        refetchMessagesRef.current();
      }
      hasConnectedBefore = true;
    };

    socket.onclose = (event) => {
      if ([4001, 4003].includes(event.code)) {
        console.warn(
          `AI chat WebSocket rejected (${event.code}) — stopping reconnect`,
        );
        socket.close();
        socketRef.current = null;
      }
    };

    socket.onerror = (e) => {
      console.warn("AI chat WebSocket error:", e);
    };

    const heartbeat = window.setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ command: "ping" }));
      }
    }, 30_000);

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const eventType = data?.command || data?.type || data?.event;
        if (eventType === "ai_chat_message") {
          const incoming =
            data?.message || data?.assistant_message || data?.data || data;
          const nextMessage = normalizeMessage(incoming, "assistant");
          if (nextMessage) {
            const currentPendingId = pendingMessageIdRef.current;
            updateMessagesCache((prev) => {
              const cleaned = prev.items.filter(
                (m) => !(m?.status === "loading" && m?.client_message_id === currentPendingId),
              );
              if (nextMessage.id && cleaned.some((m) => m?.id === nextMessage.id)) {
                return {
                  ...prev,
                  items: cleaned.map((m) =>
                    m?.id === nextMessage.id ? { ...m, ...nextMessage } : m,
                  ),
                };
              }
              const enriched = currentPendingId
                ? { ...nextMessage, client_message_id: currentPendingId }
                : nextMessage;
              return { ...prev, items: [...cleaned, enriched], total: cleaned.length + 1 };
            });
            const messageStatus = String(nextMessage?.status || "").toLowerCase();
            if (messageStatus !== "loading" && messageStatus !== "streaming") {
              setPendingAssistant(false);
              setPendingMessageId(null);
              if (getMessageRole(nextMessage) === "assistant") clearAgentProgress();
            }
          }
          if (data?.intent) {
            setIntentData(data.intent);
            if (data.intent.sandbox_job_id) clearSandboxProgress(data.intent.sandbox_job_id);
          }
        }
        if (eventType === "ai_chat_contract_status") {
          const status = String(data?.status || "").toLowerCase();
          const progressValue =
            typeof data?.progress === "number" ? data.progress : 0;
          const message = data?.message || "";
          const inProgress = isContractInProgress(status);
          setContractState((prev) => ({
            ...prev,
            status: status || prev.status,
            progress: inProgress ? progressValue : (progressValue ?? prev.progress),
            progressMessage: message || prev.progressMessage,
            error: inProgress ? null : (data?.error || prev.error),
          }));
          const nextContractId = data?.contract_id;
          if (nextContractId) {
            setContractId(nextContractId);
          }
          if (
            (status === "completed" || status === "successful") &&
            (nextContractId || contractIdRef.current)
          ) {
            refetchContractRef.current();
          }
        }
        if (eventType === "sandbox_job_status") {
          setSandboxProgress(data, activeSessionId);
        }
        if (eventType === "ai_chat_agent_progress") {
          setAgentProgress(data, activeSessionId);
        }
        if (eventType === "ai_chat_stopped") {
          const deletedMessageId = data?.deleted_message_id;
          const stoppedSessionId = data?.session_id;
          if (stoppedSessionId && String(stoppedSessionId) === String(activeSessionId)) {
            if (deletedMessageId) {
              updateMessagesCache((prev) => {
                const cleaned = prev.items.filter(
                  (m) => String(m?.id) !== String(deletedMessageId),
                );
                return { ...prev, items: cleaned, total: cleaned.length };
              });
            }
            setPendingAssistant(false);
            setPendingMessageId(null);
            clearAgentProgress();
          }
        }
        if (eventType === "grant_changed") {
          queryClient.invalidateQueries({
            queryKey: queryKeys.AIChatGrant(activeSessionId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.AIChatAccessRequests(activeSessionId),
          });
          refetchMessagesRef.current();
        }
        if (eventType === "access_request_changed") {
          queryClient.invalidateQueries({
            queryKey: queryKeys.AIChatAccessRequests(activeSessionId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.AIChatGrant(activeSessionId),
          });
          refetchMessagesRef.current();
        }
      } catch (err) {
        console.error("Failed to parse AI chat socket message", err);
      }
    };
    return () => {
      window.clearInterval(heartbeat);
      socket.close();
      socketRef.current = null;
    };
  }, [accessToken, activeSessionId, updateMessagesCache, setPendingAssistant, setPendingMessageId, setIntentData, setContractState, setContractId, setSandboxProgress, clearSandboxProgress, setAgentProgress, clearAgentProgress, queryClient]);

  return children;
};
// eslint-disable-next-line react-refresh/only-export-components
export { DraftFlowProvider, useDraftFlowData, useDraftFlowActions, useDraftFlowProgressContext, getMessageRole };
